import { Injectable } from '@angular/core';
import { AppContextService } from '../context/AppContextService ';

export type TaskTimeDisplayFormat = 'hh_mm' | 'hundredths';

@Injectable({ providedIn: 'root' })
export class TaskTimeDisplayService {
  private format: TaskTimeDisplayFormat = 'hh_mm';

  constructor(private appContextService: AppContextService) {
    this.appContextService.context$.subscribe((context) => {
      const value = String(context?.config?.TASK_TIME_DISPLAY_FORMAT ?? '')
        .trim()
        .toLowerCase();
      this.format =
        value === 'hundredths' || value === 'centième' || value === 'centieme'
          ? 'hundredths'
          : 'hh_mm';
    });
  }

  formatMinutes(minutes: number | null | undefined): string {
    const safeMinutes = Math.max(0, Math.round(Number(minutes) || 0));

    if (this.format === 'hundredths') {
      return (safeMinutes / 60).toFixed(2).replace('.', ',');
    }

    const hours = Math.floor(safeMinutes / 60);
    const remainingMinutes = safeMinutes % 60;

    return `${String(hours).padStart(2, '0')}:${String(
      remainingMinutes
    ).padStart(2, '0')}`;
  }

  formatInput(minutes: number | null | undefined): string {
    return this.formatMinutes(minutes);
  }

  isHundredths(): boolean {
    return this.format === 'hundredths';
  }

  parseInput(value: unknown): number {
    if (typeof value === 'number') {
      return this.format === 'hundredths'
        ? Math.round(value * 60)
        : Math.round(value);
    }

    const text = String(value ?? '').trim();
    if (!text) {
      return 0;
    }

    if (this.format === 'hundredths' && !text.includes(':')) {
      const decimalHours = Number(text.replace(',', '.'));
      return Number.isFinite(decimalHours) ? Math.round(decimalHours * 60) : 0;
    }

    const [hours, minutes] = text.split(':').map(Number);
    return Number.isFinite(hours) && Number.isFinite(minutes)
      ? hours * 60 + minutes
      : 0;
  }

  formatPlanningTotal(minutes: number | null | undefined): string {
    if (this.format === 'hundredths') {
      return this.formatMinutes(minutes);
    }

    const safeMinutes = Math.max(0, Math.round(Number(minutes) || 0));
    if (safeMinutes < 60) {
      return `${safeMinutes} min`;
    }

    const hours = Math.floor(safeMinutes / 60);
    const remainingMinutes = safeMinutes % 60;

    return remainingMinutes === 0
      ? `${hours}h`
      : `${hours}h${String(remainingMinutes).padStart(2, '0')}`;
  }
}
